import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const WordAssociationNetwork = ({ category, reviews }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedWord, setSelectedWord] = useState(null);
  
  // Generate network data
  useEffect(() => {
    if (!svgRef.current || !reviews || !reviews.length || !category) return;
    
    // Clear previous visualization
    d3.select(svgRef.current).selectAll("*").remove();
    
    // Filter reviews for this category
    const categoryReviews = reviews.filter(review => 
      review.categorySentiments.some(cs => cs.category === category)
    );
    
    if (!categoryReviews.length) return;
    
    // Word processing - in a real implementation, this would use NLP for proper analysis
    // Here we're creating a simplified word network for demonstration
    
    // Generate word network data
    const words = {};
    const connections = {};
    
    // Helper to clean text and extract words
    const extractWords = (text) => {
      const stopWords = new Set([
        "a", "an", "the", "and", "or", "but", "in", "on", "at", "to", "for", "with", 
        "by", "about", "as", "of", "is", "was", "were", "be", "been", "being", "have", 
        "has", "had", "do", "does", "did", "i", "you", "he", "she", "it", "we", "they",
        "this", "that", "these", "those", "my", "your", "his", "her", "its", "our", "their"
      ]);
      
      return text
        .toLowerCase()
        .replace(/[^\w\s]/gi, '') // Remove punctuation
        .split(/\s+/)             // Split by whitespace
        .filter(word => 
          word.length > 2 &&      // Only words with 3+ characters
          !stopWords.has(word)    // Skip stop words
        );
    };
    
    // Process each review
    categoryReviews.forEach(review => {
      const sentiment = review.categorySentiments.find(cs => cs.category === category)?.sentiment || 'Neutral';
      const extractedWords = extractWords(review.reviewText);
      
      // Count word occurrences and track sentiment
      extractedWords.forEach(word => {
        if (!words[word]) {
          words[word] = { 
            text: word, 
            count: 0, 
            positive: 0, 
            negative: 0, 
            neutral: 0
          };
        }
        
        words[word].count += 1;
        if (sentiment === 'Positive') {
          words[word].positive += 1;
        } else if (sentiment === 'Negative') {
          words[word].negative += 1;
        } else {
          words[word].neutral += 1;
        }
        
        // Track word connections (co-occurrences)
        extractedWords.forEach(otherWord => {
          if (word === otherWord) return;
          
          const connectionKey = [word, otherWord].sort().join('_');
          if (!connections[connectionKey]) {
            connections[connectionKey] = {
              source: word,
              target: otherWord,
              value: 0
            };
          }
          connections[connectionKey].value += 1;
        });
      });
    });
    
    // Filter to get most significant words and connections
    const topWords = Object.values(words)
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
    
    const topWordSet = new Set(topWords.map(w => w.text));
    
    const topConnections = Object.values(connections)
      .filter(conn => 
        topWordSet.has(conn.source) && 
        topWordSet.has(conn.target) &&
        conn.value > 1  // At least 2 co-occurrences
      )
      .sort((a, b) => b.value - a.value)
      .slice(0, 30);
    
    // Prepare nodes with sentiment information
    const nodes = topWords.map(word => ({
      id: word.text,
      count: word.count,
      sentiment: word.positive > word.negative 
        ? 'positive' 
        : word.negative > word.positive 
          ? 'negative' 
          : 'neutral',
      positiveRatio: word.positive / word.count,
      negativeRatio: word.negative / word.count,
      neutralRatio: word.neutral / word.count
    }));
    
    // Prepare links from connections
    const links = topConnections.map(conn => ({
      source: conn.source,
      target: conn.target,
      value: conn.value
    }));
    
    // Create the network visualization
    drawNetwork(nodes, links);
  }, [reviews, category]);
  
  // Handle container resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      if (svgRef.current && containerRef.current) {
        d3.select(svgRef.current)
          .attr('width', containerRef.current.clientWidth)
          .attr('height', 400);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Network visualization drawing function
  const drawNetwork = (nodes, links) => {
    if (!nodes.length) return;
    
    const svg = d3.select(svgRef.current);
    const width = containerRef.current.clientWidth;
    const height = 400;
    
    svg.attr('width', width).attr('height', height);
    
    // Create a force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => Math.sqrt(d.count) * 3 + 10));
    
    // Draw links
    const link = svg.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke-width', d => Math.sqrt(d.value))
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6);
    
    // Draw nodes
    const node = svg.append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended))
      .on('mouseover', (event, d) => setSelectedWord(d))
      .on('mouseout', () => setSelectedWord(null))
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedWord(prevWord => prevWord && prevWord.id === d.id ? null : d);
      });
    
    // Add node circles with sentiment color
    node.append('circle')
      .attr('r', d => Math.sqrt(d.count) * 3 + 5)
      .attr('fill', d => {
        if (d.sentiment === 'positive') return '#22c55e';
        if (d.sentiment === 'negative') return '#ef4444';
        return '#94a3b8';
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);
    
    // Add node labels
    node.append('text')
      .text(d => d.id)
      .attr('x', 0)
      .attr('y', d => -Math.sqrt(d.count) * 3 - 7)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', 'medium')
      .attr('fill', '#4b5563');
    
    // Update node and link positions on simulation tick
    simulation.on('tick', () => {
      // Constrain nodes to visualization area
      nodes.forEach(node => {
        node.x = Math.max(50, Math.min(width - 50, node.x));
        node.y = Math.max(50, Math.min(height - 50, node.y));
      });
      
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      
      node
        .attr('transform', d => `translate(${d.x},${d.y})`);
    });
    
    // Drag event handlers
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    
    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }
    
    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
  };
  
  return (
    <div className="">
      <h3 className="text-md font-medium text-gray-900 mb-2">Word Association Network: {category}</h3>
      
      {/* Network Visualization */}
      <div 
        ref={containerRef} 
        className="border border-gray-200 rounded-lg bg-gray-50 mb-4 relative" 
        style={{ height: '400px' }}
      >
        <svg ref={svgRef} width="100%" height="400"></svg>
        
        {!reviews || reviews.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            No review data available for this category
          </div>
        ) : null}
      </div>
      
      {/* Network Legend */}
      <div className="flex flex-wrap justify-between items-start">
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Network Legend</h4>
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span className="text-xs text-gray-600">Positive Sentiment Words</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span className="text-xs text-gray-600">Negative Sentiment Words</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-gray-400 mr-2"></div>
              <span className="text-xs text-gray-600">Neutral Sentiment Words</span>
            </div>
            <div className="flex items-center mt-2">
              <div className="w-6 h-0.5 bg-gray-400 mr-2"></div>
              <span className="text-xs text-gray-600">Word Co-occurrence (Line Thickness = Frequency)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full border border-gray-400 mr-2"></div>
              <span className="text-xs text-gray-600">Word Size = Frequency in Reviews</span>
            </div>
          </div>
        </div>
        
        {/* Selected Word Details */}
        {selectedWord && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 w-full sm:w-64">
            <h4 className="text-sm font-medium text-gray-800 mb-2">"{selectedWord.id}"</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Mentions:</span>
                <span className="font-medium">{selectedWord.count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sentiment:</span>
                <span className={`font-medium ${
                  selectedWord.sentiment === 'positive' ? 'text-green-600' :
                  selectedWord.sentiment === 'negative' ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {selectedWord.sentiment.charAt(0).toUpperCase() + selectedWord.sentiment.slice(1)}
                </span>
              </div>
              <div className="mt-2">
                <span className="text-gray-600 text-xs">Sentiment Breakdown:</span>
                <div className="mt-1 w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="flex h-full">
                    <div 
                      className="h-full bg-green-500" 
                      style={{ width: `${selectedWord.positiveRatio * 100}%` }}
                    ></div>
                    <div 
                      className="h-full bg-red-500" 
                      style={{ width: `${selectedWord.negativeRatio * 100}%` }}
                    ></div>
                    <div 
                      className="h-full bg-gray-400" 
                      style={{ width: `${selectedWord.neutralRatio * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-green-500">{Math.round(selectedWord.positiveRatio * 100)}%</span>
                  <span className="text-red-500">{Math.round(selectedWord.negativeRatio * 100)}%</span>
                  <span className="text-gray-500">{Math.round(selectedWord.neutralRatio * 100)}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="text-xs text-gray-500 mt-4">
        <p>Tip: Click and drag words to rearrange the network. Click on a word to see detailed stats.</p>
      </div>
    </div>
  );
};

export default WordAssociationNetwork;